/**
 * First-login change-password screen.
 *
 * Reached automatically whenever `user.force_password_change === true`
 * — see `ProtectedRoute`, which redirects here and blocks every other
 * authenticated route until the flag clears. Also linked from the
 * Profile screen so any member can update their password voluntarily.
 */
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, useApi } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

export default function ChangePasswordPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { user, refresh, signOut } = useAuth();
  const api = useApi();
  const forced = !!user?.force_password_change;
  const from = sp.get("from") || "/events";

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!forced && !current) return setError("Enter your current password.");
    if (next.length < 6) return setError("New password must be at least 6 characters.");
    if (next !== confirm) return setError("New password and confirmation don't match.");
    if (!forced && next === current) return setError("New password must be different from the current one.");
    setLoading(true);
    try {
      const body = forced
        ? { new_password: next }
        : { current_password: current, new_password: next };
      const r = await api("/auth/change-password", {
        method: "POST",
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Password change failed");
      // Backend bumped token_version to invalidate every other
      // outstanding session; the response carries a fresh token
      // stamped with the new version so THIS session survives.
      if (data.token && data.user) {
        await setSession(data.token, data.user);
      } else {
        await refresh();
      }
      toast.success("Password updated");
      nav(forced ? "/events" : from, { replace: true });
    } catch (err) {
      setError(err.message || "Password change failed");
    } finally {
      setLoading(false);
    }
  };

  const onCancel = async () => {
    if (forced) {
      // Forced-change users can't skip — offer sign-out as the only
      // escape hatch so they aren't trapped on the screen.
      await signOut();
      nav("/login", { replace: true });
      return;
    }
    nav(-1);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <img src="/mc-crest.png" alt="" className="mx-auto mb-3 h-16 w-auto" />
          <h1 className="text-2xl font-semibold">
            {forced ? "Set your password" : "Change password"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {forced
              ? "You're signed in with a temporary password. Choose a permanent password to continue."
              : "Update the password you use to sign in to Middlesex Connect."}
          </p>
        </div>
        <Card>
          <CardContent className="p-6">
            <form onSubmit={onSubmit} className="space-y-4">
              {!forced && (
                <div className="space-y-1">
                  <Label htmlFor="current">Current password</Label>
                  <Input
                    id="current"
                    data-testid="change-current"
                    type="password"
                    autoComplete="current-password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="next">New password</Label>
                <Input
                  id="next"
                  data-testid="change-new"
                  type="password"
                  autoComplete="new-password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  placeholder="At least 6 characters"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input
                  id="confirm"
                  data-testid="change-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-type new password"
                />
              </div>
              {error && (
                <div className="text-sm text-destructive" data-testid="change-error">
                  {error}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1"
                  onClick={onCancel}
                  data-testid="change-cancel"
                >
                  {forced ? "Sign out" : "Cancel"}
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1"
                  data-testid="change-submit"
                >
                  {loading ? "Saving…" : "Update password"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
